import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TextField, Typography } from '@mui/material';
import ConfirmFormModal, { ConfirmModalType } from './ConfirmFormModal';
import AccountDeletionService from 'services/account-deletion.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import capitalize from 'utils/capitalize';

/** Same rule the backend uses to refuse a verification delete — nothing on file, nothing to delete. */
export const hasIdentityVerification = member =>
    Boolean(member?.identity_verified === true || member?.photo_id || member?.id_photo);

/**
 * Shared "Delete verification" confirmation modal — used from every user-listing table
 * (Occupants, Tenants, Guests). Removes only the user's identity verification (ID photos,
 * verified flag); the account itself stays. The typed email only gates the Confirm button
 * here; the backend re-verifies it server-side, so this always sends `member.email`.
 */
const DeleteVerificationModal = ({ open, onClose, member, onDeleted }) => {
    const [typedEmail, setTypedEmail] = useState('');
    const displayName = `${capitalize(member?.first_name)} ${capitalize(member?.last_name)}`.trim();

    const handleClose = () => {
        setTypedEmail('');
        onClose();
    };

    const handleConfirm = async () => {
        await AccountDeletionService.deleteVerification(member.uid, member.email);
        SnackbarNotify.success(`${displayName}'s identity verification has been deleted.`);
        setTypedEmail('');
        onDeleted?.();
    };

    if (!member) return null;

    return (
        <ConfirmFormModal
            open={open}
            onClose={handleClose}
            onConfirm={handleConfirm}
            type={ConfirmModalType.WARNING}
            title="Delete identity verification?"
            text={`This permanently deletes ${displayName}'s ID document photos and marks the account as not verified. The account itself is kept, and the user will be notified by email. This cannot be undone.`}
            confirmText="Delete Verification"
            loadingText="Deleting..."
            disableConfirm={typedEmail !== member.email}
        >
            <Typography variant="caption" color="textSecondary">
                Type <strong>{member.email}</strong> to confirm.
            </Typography>
            <TextField
                fullWidth
                autoFocus
                placeholder={member.email}
                value={typedEmail}
                onChange={e => setTypedEmail(e.target.value)}
            />
        </ConfirmFormModal>
    );
};

DeleteVerificationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    member: PropTypes.shape({
        uid: PropTypes.string,
        email: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string
    }),
    onDeleted: PropTypes.func
};

export default DeleteVerificationModal;
